import * as React from 'react';
import { observer } from 'mobx-react';
import { Button, Modal, RadioButtonGroup } from '@ixicare/ui';
import { t } from '~/i18n';
import { AlarmEventType, AlarmResolveVerificationMode } from '@ixicare/resources';
import { fallReasons, resolveReasons } from '~config/alert';
import Alert from '~store/Alert/Alert';
import { useSiteConfiguration } from '~containers/ConfigurationForm/ConfigurationForm';
import { useStores } from '~store/utils';

interface AlertResolveModalProps {
  alert: Alert;
  loading: boolean;
  onClose: () => void;
  onResolve: (reason: string) => void;
}

const AlertResolveModal: React.FC<AlertResolveModalProps> = observer(({ alert, loading, onClose, onResolve }) => {
  const [reason, setReason] = React.useState<string>();
  const [isFall, setFall] = React.useState(false);
  const { permissions } = useStores();
  const hasConfigurationPermission = permissions.has('can_edit_site_configuration');
  const { data: siteConfig } = useSiteConfiguration();

  const handleSave = React.useCallback(() => {
    if (
      (alert.eventType === AlarmEventType.Manual && reason === 'undetected-fall') ||
      (alert.hasFallEvent && reason === 'nature-of-call-resolved')
    ) {
      setFall(true);
      setReason(undefined);
    } else {
      onResolve(reason!);
    }
  }, [reason, alert]);

  const handleSaveFall = React.useCallback(() => {
    onResolve(`Fall: ${reason!}`);
  }, [reason]);

  const isProximityControlEnforced =
    siteConfig?.['alarm-resolve-verification-mode'] === AlarmResolveVerificationMode.DeviceVerification &&
    siteConfig?.['require-proximity-control'];

  if (isProximityControlEnforced && !hasConfigurationPermission) {
    return (
      <Modal onClose={onClose} style={{ maxWidth: 450 }}>
        <h3 className="text-center mb-30">{t('alert.resolve.proximityControl.title')}</h3>
        <p className="text-center mb-30">{t('alert.resolve.proximityControl.message')}</p>
      </Modal>
    );
  }

  return (
    <Modal onClose={onClose} style={{ maxWidth: 450 }}>
      <h3 className="text-center mb-30">{t(`alert.resolve.title`)}</h3>

      <RadioButtonGroup
        name="resolve-reasons"
        options={isFall ? fallReasons : (resolveReasons[alert.eventType] ?? resolveReasons.default)}
        value={reason}
        onChange={setReason}
        withOtherOption
      />

      <Button
        className="mt-15 mb-15"
        loading={loading}
        isBlock
        disabled={loading || !reason?.length}
        onClick={isFall ? handleSaveFall : handleSave}
      >
        {t(`alert.resolve.submit`)}
      </Button>
    </Modal>
  );
});

export default AlertResolveModal;
